/**
 * PartnerPositionsPage - Страница управления должностями партнёров
 *
 * Доступ: Admin, Manager
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Title,
  Button,
  Group,
  Table,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Box,
  Stack,
} from '@mantine/core';
import {
  IconPlus,
  IconDots,
  IconEye,
  IconPencil,
  IconTrash,
  IconBriefcase,
  IconCheck,
  IconLock,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PositionFormModal from '../components/PartnerPositions/PositionFormModal';
import PositionViewModal from '../components/PartnerPositions/PositionViewModal';
import tokens from '../theme/designTokens';

/**
 * API функции для должностей
 */
const positionsApi = {
  async getPositions(includeInactive = true) {
    const response = await fetch(`/api/partner-positions?include_inactive=${includeInactive}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить должности');
    }
    return response.json();
  },

  async deletePosition(id) {
    const response = await fetch(`/api/partner-positions/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить должность');
    }
  },
};

/**
 * Форматирование даты
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

const PartnerPositionsPage = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Модальное окно формы
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingPosition, setViewingPosition] = useState(null);

  // Загрузка должностей
  const loadPositions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await positionsApi.getPositions(true);
      setPositions(data || []);
    } catch (err) {
      console.error('Failed to load positions:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  // Открыть форму создания
  const handleCreate = () => {
    setEditingPosition(null);
    setFormModalOpen(true);
  };

  // Открыть форму редактирования
  const handleEdit = (position) => {
    setEditingPosition(position);
    setFormModalOpen(true);
  };

  // Открыть просмотр
  const handleView = (position) => {
    setViewingPosition(position);
    setViewModalOpen(true);
  };

  // Удалить должность
  const handleDelete = async (position) => {
    if (position.is_system) {
      notifications.show({
        title: 'Ошибка',
        message: 'Нельзя удалить системную должность',
        color: 'red',
      });
      return;
    }

    if (!window.confirm(`Удалить должность "${position.name}"?`)) {
      return;
    }

    try {
      await positionsApi.deletePosition(position.id);
      notifications.show({
        title: 'Успешно',
        message: 'Должность удалена',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      await loadPositions();
    } catch (err) {
      console.error('Failed to delete position:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    }
  };

  // После сохранения формы
  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setEditingPosition(null);
    loadPositions();
  };

  // Редактирование из модалки просмотра
  const handleEditFromView = (position) => {
    setViewModalOpen(false);
    setViewingPosition(null);
    setEditingPosition(position);
    setFormModalOpen(true);
  };

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Должности партнёров
            </Title>
            <Text c="dimmed" size="sm">
              Справочник должностей для контактов организаций
            </Text>
          </Box>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
          >
            Добавить должность
          </Button>
        </Group>

        {/* Таблица */}
        {loading ? (
          <Center py="xl">
            <Loader size="md" />
          </Center>
        ) : positions.length === 0 ? (
          <Center py="xl">
            <Box style={{ textAlign: 'center' }}>
              <IconBriefcase
                size={48}
                color={tokens.colors.neutral[4]}
                style={{ marginBottom: '16px' }}
              />
              <Text size="lg" c="dimmed" mb="xs">
                Должности не найдены
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                Создайте должности для использования в контактах
              </Text>
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={handleCreate}
              >
                Добавить должность
              </Button>
            </Box>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 60 }}>Порядок</Table.Th>
                <Table.Th style={{ width: 300 }}>Название</Table.Th>
                <Table.Th style={{ width: 120 }}>Тип</Table.Th>
                <Table.Th style={{ width: 100 }}>Статус</Table.Th>
                <Table.Th style={{ width: 180 }}>Создана</Table.Th>
                <Table.Th style={{ width: 60 }}>Действия</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {positions.map((position) => (
                <Table.Tr
                  key={position.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleView(position)}
                >
                  <Table.Td>
                    <Text size="sm" c="dimmed">{position.sort_order}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <IconBriefcase size={16} color={tokens.colors.brand.primary} />
                      <Text size="sm" fw={500}>{position.name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {position.is_system ? (
                      <Badge
                        color="violet"
                        variant="light"
                        radius="xl"
                        size="sm"
                        leftSection={<IconLock size={10} />}
                      >
                        Системная
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="xl" size="sm">
                        Пользовательская
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {position.is_active ? (
                      <Badge color="green" variant="light" radius="xl" size="sm">
                        Активна
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="xl" size="sm">
                        Неактивна
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatDate(position.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item
                          leftSection={<IconEye size={14} />}
                          onClick={() => handleView(position)}
                        >
                          Просмотр
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() => handleEdit(position)}
                        >
                          Редактировать
                        </Menu.Item>
                        {!position.is_system && (
                          <>
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconTrash size={14} />}
                              color="red"
                              onClick={() => handleDelete(position)}
                            >
                              Удалить
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      {/* Модальное окно формы */}
      <PositionFormModal
        opened={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingPosition(null);
        }}
        onSuccess={handleFormSuccess}
        position={editingPosition}
      />

      {/* Модальное окно просмотра */}
      <PositionViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingPosition(null);
        }}
        position={viewingPosition}
        onEdit={handleEditFromView}
      />
    </Box>
  );
};

export default PartnerPositionsPage;
